/**
 * useStatistics Hook
 * 통계 데이터 조회 및 분석 커스텀 훅
 */

import { useState, useCallback } from 'react';
import { StatisticsRepository } from '../repositories/StatisticsRepository';
import { StatisticsService } from '../services/StatisticsService';
import {
  DashboardStats,
  RevenueStats,
  OrganizationStats,
  OwnerTrendStats,
  ServiceType,
} from '../models';

const statisticsRepository = new StatisticsRepository();
const statisticsService = new StatisticsService(statisticsRepository);

export const useStatistics = (serviceType?: ServiceType) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(
    null
  );
  const [revenueStats, setRevenueStats] = useState<RevenueStats | null>(null);
  const [organizationStats, setOrganizationStats] =
    useState<OrganizationStats | null>(null);
  const [ownerTrendStats, setOwnerTrendStats] =
    useState<OwnerTrendStats | null>(null);

  /**
   * 대시보드 통계 조회
   */
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await statisticsService.getDashboardStats(serviceType);
      setDashboardStats(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [serviceType]);

  /**
   * 매출 통계 조회
   */
  const fetchRevenue = useCallback(
    async (startDate?: string, endDate?: string) => {
      setLoading(true);
      setError(null);

      try {
        if (
          startDate &&
          endDate &&
          !statisticsService.validateDateRange(startDate, endDate)
        ) {
          throw new Error('시작일은 종료일보다 이전이어야 합니다.');
        }

        const data = await statisticsService.getRevenueStats(
          startDate,
          endDate,
          serviceType
        );
        setRevenueStats(data);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '조회에 실패했습니다.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [serviceType]
  );

  /**
   * 사업자 통계 조회
   */
  const fetchOrganization = useCallback(
    async (startDate?: string, endDate?: string) => {
      setLoading(true);
      setError(null);

      try {
        if (
          startDate &&
          endDate &&
          !statisticsService.validateDateRange(startDate, endDate)
        ) {
          throw new Error('시작일은 종료일보다 이전이어야 합니다.');
        }

        const data = await statisticsService.getOrganizationStats(
          startDate,
          endDate,
          serviceType
        );
        setOrganizationStats(data);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '조회에 실패했습니다.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [serviceType]
  );

  /**
   * 대시보드 요약 정보 생성
   */
  const getDashboardSummary = useCallback(() => {
    if (!dashboardStats) return null;
    return statisticsService.generateDashboardSummary(dashboardStats);
  }, [dashboardStats]);

  /**
   * 매출 차트 데이터 생성
   */
  const getRevenueChartData = useCallback(() => {
    if (!revenueStats) return null;
    return statisticsService.generateRevenueChart(revenueStats);
  }, [revenueStats]);

  /**
   * 사업자 가입 추이 차트 생성
   */
  const getOrganizationTrendChart = useCallback(() => {
    if (!organizationStats) return null;
    return statisticsService.generateOrganizationTrendChart(
      organizationStats
    );
  }, [organizationStats]);

  /**
   * 승인율 계산
   */
  const getApprovalRate = useCallback(() => {
    if (!dashboardStats) return 0;
    return statisticsService.calculateApprovalRate(dashboardStats);
  }, [dashboardStats]);

  /**
   * 완료율 계산 (문의)
   */
  const getCompletionRate = useCallback(() => {
    if (!dashboardStats) return 0;
    return statisticsService.calculateCompletionRate(dashboardStats);
  }, [dashboardStats]);

  /**
   * 활성 구독률 계산
   */
  const getActiveSubscriptionRate = useCallback(() => {
    if (!dashboardStats) return 0;
    return statisticsService.calculateActiveSubscriptionRate(dashboardStats);
  }, [dashboardStats]);

  /**
   * 월별 매출 성장률 계산
   */
  const getRevenueGrowthRate = useCallback(() => {
    if (!revenueStats) return 0;
    return statisticsService.calculateRevenueGrowthRate(revenueStats);
  }, [revenueStats]);

  /**
   * 기본 날짜 범위 가져오기 (최근 30일)
   */
  const getDefaultDateRange = useCallback(() => {
    return statisticsService.getDefaultDateRange();
  }, []);

  /**
   * 사업자 트렌드 통계 조회 (차트용)
   */
  const fetchOwnerTrend = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await statisticsRepository.getOwnerTrendStats();
      setOwnerTrendStats(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '사업자 트렌드 통계 조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    dashboardStats,
    revenueStats,
    organizationStats,
    ownerTrendStats,
    fetchDashboard,
    fetchRevenue,
    fetchOrganization,
    fetchOwnerTrend,
    getDashboardSummary,
    getRevenueChartData,
    getOrganizationTrendChart,
    getApprovalRate,
    getCompletionRate,
    getActiveSubscriptionRate,
    getRevenueGrowthRate,
    getDefaultDateRange,
  };
};
